import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ChevronLeft, Shield, FileText, Scale } from "lucide-react";
import { Logo } from "@/components/Logo";

export function LegalShell({ title, icon: Icon, children }) {
  const { i18n } = useTranslation();
  const es = i18n.language?.startsWith("es");
  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      <header className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-4">
            <Logo />
          </Link>
          <Link to="/" className="text-xs font-mono uppercase tracking-wider text-slate-500 hover:text-[#003366] inline-flex items-center gap-1" data-testid="legal-back">
            <ChevronLeft size={14} /> {es ? "Volver" : "Back"}
          </Link>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-6 py-10">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-[#003366] text-white flex items-center justify-center">
            <Icon size={20} />
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">{title}</h1>
        </div>
        <article className="prose prose-slate max-w-none bg-white border border-slate-200 rounded-xl p-8 text-sm leading-relaxed text-slate-700 space-y-4 [&_h2]:font-display [&_h2]:font-bold [&_h2]:text-slate-900 [&_h2]:text-lg [&_h2]:mt-6 [&_h2]:mb-2 [&_h3]:font-semibold [&_h3]:text-slate-900 [&_h3]:text-base [&_h3]:mt-4 [&_h3]:mb-1 [&_ul]:list-disc [&_ul]:pl-6 [&_li]:mt-1">
          {children}
        </article>
      </main>
    </div>
  );
}

export function PrivacyPolicy() {
  const { i18n } = useTranslation();
  const es = i18n.language?.startsWith("es");
  return (
    <LegalShell title={es ? "Política de Privacidad" : "Privacy Policy"} icon={Shield}>
      <p className="text-xs font-mono uppercase tracking-wider text-slate-500">
        {es ? "Última actualización: enero 2026" : "Last updated: January 2026"}
      </p>
      {es ? (
        <>
          <h2>1. Información que recopilamos</h2>
          <p>IQES LLC ("nosotros") recopila información que usted proporciona directamente al usar IQ-APP: nombre, correo electrónico, empresa, contraseña cifrada (bcrypt), archivos subidos (planos PDF, imágenes, site surveys), datos de proyectos y anotaciones generadas.</p>
          <h2>2. Uso de la información</h2>
          <ul>
            <li>Proveer el servicio de estimación asistida por IA.</li>
            <li>Enviar notificaciones operacionales (no marketing sin consentimiento).</li>
            <li>Mejorar el modelo con datos agregados y anonimizados.</li>
            <li>Cumplimiento con obligaciones legales (Florida DBPR, IRS, etc.).</li>
          </ul>
          <h2>3. Integración con IA (Claude Sonnet 4.5)</h2>
          <p>Los planos, fotos y textos subidos se envían de forma segura a Anthropic (Claude) únicamente para generar el análisis técnico. Por contrato, los datos no se usan para entrenar modelos de terceros.</p>
          <h2>4. Derechos del usuario (CCPA / GDPR-like)</h2>
          <ul>
            <li>Acceso, rectificación y eliminación de sus datos.</li>
            <li>Exportación en formato portable (JSON/CSV/PDF).</li>
            <li>Revocación del consentimiento en cualquier momento.</li>
            <li>Contacto: privacy@iqeslowvoltage.com</li>
          </ul>
          <h2>5. Retención de datos</h2>
          <p>Conservamos los datos activos mientras la cuenta esté activa + 1 año por obligaciones fiscales. Luego se eliminan o anonimizan.</p>
          <h2>6. Seguridad</h2>
          <p>JWT con expiración de 7 días, contraseñas hasheadas con bcrypt, TLS en tránsito, backups cifrados en reposo, acceso a datos por rol (admin/estimator/viewer).</p>
          <h2>7. Niños</h2>
          <p>IQ-APP es una herramienta profesional B2B. No recolectamos datos de menores de 18 años.</p>
          <h2>8. Cambios</h2>
          <p>Notificaremos en la app cambios materiales con 30 días de anticipación.</p>
        </>
      ) : (
        <>
          <h2>1. Information we collect</h2>
          <p>IQES LLC ("we") collects information you provide directly when using IQ-APP: name, email, company, bcrypt-hashed password, uploaded files (PDF blueprints, images, site surveys), project data and generated annotations.</p>
          <h2>2. Use of information</h2>
          <ul>
            <li>Provide the AI-assisted estimation service.</li>
            <li>Send operational notifications (no marketing without consent).</li>
            <li>Improve the model using aggregated, anonymized data.</li>
            <li>Comply with legal obligations (Florida DBPR, IRS, etc.).</li>
          </ul>
          <h2>3. AI integration (Claude Sonnet 4.5)</h2>
          <p>Blueprints, photos and text you upload are sent securely to Anthropic (Claude) only to generate the technical analysis. By contract, data is not used to train third-party models.</p>
          <h2>4. User rights (CCPA / GDPR-like)</h2>
          <ul>
            <li>Access, rectification and deletion of your data.</li>
            <li>Export in portable formats (JSON/CSV/PDF).</li>
            <li>Revoke consent at any time.</li>
            <li>Contact: privacy@iqeslowvoltage.com</li>
          </ul>
          <h2>5. Data retention</h2>
          <p>We keep active data while the account is active + 1 year for tax obligations. Then data is deleted or anonymized.</p>
          <h2>6. Security</h2>
          <p>JWT with 7-day expiration, bcrypt password hashing, TLS in transit, encrypted backups at rest, role-based access (admin/estimator/viewer).</p>
          <h2>7. Children</h2>
          <p>IQ-APP is a professional B2B tool. We do not collect data from anyone under 18.</p>
          <h2>8. Changes</h2>
          <p>We will notify inside the app 30 days in advance of any material change.</p>
        </>
      )}
    </LegalShell>
  );
}

export function TermsOfUse() {
  const { i18n } = useTranslation();
  const es = i18n.language?.startsWith("es");
  return (
    <LegalShell title={es ? "Términos de Uso" : "Terms of Use"} icon={FileText}>
      <p className="text-xs font-mono uppercase tracking-wider text-slate-500">
        {es ? "Última actualización: enero 2026" : "Last updated: January 2026"}
      </p>
      {es ? (
        <>
          <h2>1. Aceptación</h2>
          <p>Al usar IQ-APP usted acepta estos términos, operados por IQES LLC, sociedad de Florida.</p>
          <h2>2. Licencia de uso</h2>
          <p>Licencia personal, no exclusiva, no transferible, para uso profesional. Prohibido revender, hacer ingeniería inversa o entrenar modelos derivados sin autorización escrita.</p>
          <h2>3. Cuenta del usuario</h2>
          <p>Usted es responsable de mantener sus credenciales seguras. Debe notificar de inmediato cualquier acceso no autorizado a privacy@iqeslowvoltage.com.</p>
          <h2>4. Uso aceptable</h2>
          <ul>
            <li>No subir contenido ilegal, confidencial sin autorización, o con derechos de autor de terceros.</li>
            <li>No usar IQ-APP para sabotaje, phishing o actividades maliciosas.</li>
            <li>No exceder los límites de uso de la suscripción.</li>
          </ul>
          <h2>5. Exención de responsabilidad técnica</h2>
          <p>IQ-APP es una herramienta de estimación <b>referencial</b>. Todo reporte debe ser validado por un Ingeniero Profesional (PE) licenciado antes de uso en construcción. IQES LLC no asume responsabilidad por decisiones tomadas sin revisión PE.</p>
          <h2>6. Limitación de responsabilidad</h2>
          <p>Responsabilidad máxima limitada al monto pagado por el usuario en los 12 meses anteriores. Sin responsabilidad por daños indirectos, consecuentes o lucro cesante.</p>
          <h2>7. Propiedad intelectual</h2>
          <p>IQES LLC conserva la propiedad del software. Usted conserva la propiedad de los archivos que suba. La salida generada (BOM, reportes) le pertenece para uso profesional.</p>
          <h2>8. Terminación</h2>
          <p>Puede cancelar su cuenta en cualquier momento. Podemos suspender cuentas que violen estos términos con notificación.</p>
          <h2>9. Ley aplicable</h2>
          <p>Estado de Florida, EE.UU. Jurisdicción exclusiva en los tribunales del Condado de Miami-Dade.</p>
        </>
      ) : (
        <>
          <h2>1. Acceptance</h2>
          <p>By using IQ-APP you accept these terms, operated by IQES LLC, a Florida corporation.</p>
          <h2>2. License</h2>
          <p>Personal, non-exclusive, non-transferable license for professional use. Resale, reverse engineering, or deriving competing models is prohibited without written authorization.</p>
          <h2>3. User account</h2>
          <p>You are responsible for keeping your credentials secure. Notify privacy@iqeslowvoltage.com immediately of any unauthorized access.</p>
          <h2>4. Acceptable use</h2>
          <ul>
            <li>Do not upload illegal, unauthorized confidential, or third-party copyrighted content.</li>
            <li>No sabotage, phishing or malicious activities.</li>
            <li>Do not exceed the limits of your subscription.</li>
          </ul>
          <h2>5. Technical disclaimer</h2>
          <p>IQ-APP is a <b>referential</b> estimation tool. Every report must be validated by a licensed Professional Engineer (PE) before construction. IQES LLC takes no responsibility for decisions made without PE review.</p>
          <h2>6. Liability cap</h2>
          <p>Maximum liability limited to the amount paid in the prior 12 months. No liability for indirect, consequential or lost-profit damages.</p>
          <h2>7. IP</h2>
          <p>IQES LLC owns the software. You own your uploaded files. Generated output (BOM, reports) is yours for professional use.</p>
          <h2>8. Termination</h2>
          <p>You may cancel at any time. We may suspend accounts that violate these terms, with notice.</p>
          <h2>9. Governing law</h2>
          <p>State of Florida, USA. Exclusive jurisdiction in Miami-Dade County courts.</p>
        </>
      )}
    </LegalShell>
  );
}

export function Compliance() {
  const { i18n } = useTranslation();
  const es = i18n.language?.startsWith("es");
  return (
    <LegalShell title={es ? "Cumplimiento & Normativas" : "Compliance & Regulatory"} icon={Scale}>
      {es ? (
        <>
          <h2>Normativas aplicables al trabajo de IQES</h2>
          <p>IQ-APP está diseñada para asistir en el cumplimiento de las principales normativas aplicables a trabajos eléctricos y de bajo voltaje en Estados Unidos, con foco en Florida.</p>
          <h3>Electrical</h3>
          <ul>
            <li><b>NEC 2026 (NFPA 70)</b> — Código Eléctrico Nacional. Obligatorio para toda instalación eléctrica.</li>
            <li><b>NFPA 70E</b> — Seguridad eléctrica en el lugar de trabajo.</li>
            <li><b>NFPA 72</b> — Alarmas de incendio y sistemas de notificación.</li>
          </ul>
          <h3>Low Voltage / ICT</h3>
          <ul>
            <li><b>ANSI/TIA-568.0-E / 568.1-E / 568.2-E / 568.3-E</b> — Cableado estructurado comercial (par trenzado y fibra).</li>
            <li><b>ANSI/TIA-569-E</b> — Rutas y espacios de telecomunicaciones.</li>
            <li><b>ANSI/TIA-606-C</b> — Administración y etiquetado (origen del esquema D1, R1-PP2-PTO22-D46).</li>
            <li><b>ANSI-J-STD-607-C</b> — Aterrizaje y enlaces para telecomunicaciones.</li>
            <li><b>BICSI TDMM / ITSIMM</b> — Mejores prácticas del sector.</li>
          </ul>
          <h3>Seguridad & Laboral</h3>
          <ul>
            <li><b>OSHA 29 CFR 1926</b> — Seguridad en construcción.</li>
            <li><b>Workers' Compensation</b> — Requerido en Florida para todos los empleados.</li>
          </ul>
          <h3>Licencias Florida</h3>
          <ul>
            <li><b>Florida DBPR Ch. 489</b> — Licencias de contratistas (EF/ET/ES/EC).</li>
            <li>Permisos AHJ locales según jurisdicción (Miami-Dade, Broward, Orange, etc.).</li>
          </ul>
          <h3>Protección de datos</h3>
          <ul>
            <li><b>FTC Safeguards Rule</b> — Protección de datos financieros.</li>
            <li><b>CCPA/CPRA</b> — Derechos de clientes en California (si aplicable).</li>
            <li><b>HIPAA</b> — Solo si el proyecto involucra instalaciones de salud (NEC Art. 517).</li>
          </ul>
          <h3>IA & uso responsable</h3>
          <p>IQ-APP usa Claude Sonnet 4.5 (Anthropic) para análisis. Todos los reportes se emiten con disclaimer de revisión PE. El modelo NO reemplaza el juicio profesional del ingeniero licenciado.</p>
        </>
      ) : (
        <>
          <h2>Regulations applicable to IQES work</h2>
          <p>IQ-APP is designed to assist compliance with the key regulations applicable to electrical and low-voltage work in the United States, with a focus on Florida.</p>
          <h3>Electrical</h3>
          <ul>
            <li><b>NEC 2026 (NFPA 70)</b> — National Electrical Code. Mandatory for any electrical installation.</li>
            <li><b>NFPA 70E</b> — Electrical safety in the workplace.</li>
            <li><b>NFPA 72</b> — Fire alarm and signaling systems.</li>
          </ul>
          <h3>Low Voltage / ICT</h3>
          <ul>
            <li><b>ANSI/TIA-568.0-E / 568.1-E / 568.2-E / 568.3-E</b> — Commercial structured cabling (twisted pair and fiber).</li>
            <li><b>ANSI/TIA-569-E</b> — Telecommunications pathways and spaces.</li>
            <li><b>ANSI/TIA-606-C</b> — Administration and labeling (source of the D1, R1-PP2-PTO22-D46 scheme).</li>
            <li><b>ANSI-J-STD-607-C</b> — Grounding and bonding for telecommunications.</li>
            <li><b>BICSI TDMM / ITSIMM</b> — Industry best practices.</li>
          </ul>
          <h3>Safety & Labor</h3>
          <ul>
            <li><b>OSHA 29 CFR 1926</b> — Construction safety.</li>
            <li><b>Workers' Compensation</b> — Required in Florida for all employees.</li>
          </ul>
          <h3>Florida licensing</h3>
          <ul>
            <li><b>Florida DBPR Ch. 489</b> — Contractor licenses (EF/ET/ES/EC).</li>
            <li>Local AHJ permits per jurisdiction (Miami-Dade, Broward, Orange, etc.).</li>
          </ul>
          <h3>Data protection</h3>
          <ul>
            <li><b>FTC Safeguards Rule</b> — Financial data protection.</li>
            <li><b>CCPA/CPRA</b> — California consumer rights (if applicable).</li>
            <li><b>HIPAA</b> — Only if project involves healthcare facilities (NEC Art. 517).</li>
          </ul>
          <h3>AI & responsible use</h3>
          <p>IQ-APP uses Claude Sonnet 4.5 (Anthropic) for analysis. All reports are issued with a PE-review disclaimer. The model does NOT replace the professional judgment of a licensed engineer.</p>
        </>
      )}
    </LegalShell>
  );
}
